// SAB Painting & Decorating — edit business details, colors, services, gallery and reviews here.
import { KITCHEN_PARTS, KITCHEN_GROUPS } from "../scenes/kitchenParts";

export const business = {
  name: "SAB Painting & Decorating",
  shortName: "SAB",
  phoneDisplay: "505-504-9827",
  phoneHref: "tel:+15055049827",
  email: "office@sabpaintingdecorating.com",
  serviceArea: "Rio Rancho, Albuquerque and surrounding areas of New Mexico",
  serviceAreaShort: ["Rio Rancho", "Albuquerque", "Surrounding New Mexico"],
  credentials: ["Military owned & operated", "Free estimates"],
  // Line shown under the logo at the end of the opening video.
  heroTagline: "Military owned · Rio Rancho & Albuquerque",
  hours: "Mon–Sat · 8am–7pm",
  siteUrl: "https://sabpaintingdecorating.com",
};

// Opening screen copy shown over the intro film.
export const hero = {
  eyebrow: "Military owned & operated · Rio Rancho & Albuquerque",
  headline: "Precision painting. Military-grade standards.",
  subline: "Interior & exterior painting, cabinets and finishing details. Free estimates · Mon–Sat 8am–7pm.",
};

export const services = {
  items: [
    { title: "Interior painting", text: "Walls, ceilings and trim for living rooms, bedrooms, hallways and more." },
    { title: "Exterior painting", text: "Stucco, siding, trim, soffits, fascia, window trim and doors." },
    { title: "Cabinet painting", text: "Kitchen and bathroom cabinetry refreshed in a new color." },
    { title: "Decorating & details", text: "Accent walls, doors and the finishing details that pull a room together." },
  ],
};

// Real SAB project photos only. Put images in /public/projects/ and add { src: "/projects/file.jpg", title, type }.
// Hidden until real photos are added: set show to true to turn the section and its nav link back on.
export const gallery = { show: false, items: [] };

// Real customer reviews only, shared with permission: { quote, name, location }. Set show to true to turn on.
export const reviews = { show: false, items: [] };

export const palettes = {
  interior: [
    { name: "Warm Bone", hex: "#EFE8DC" },
    { name: "Linen Plaster", hex: "#E3D7C4" },
    { name: "Soft Greige", hex: "#BDB4A7" },
    { name: "Mesa Sand", hex: "#D2BC9A" },
    { name: "Adobe Clay", hex: "#B86F4C" },
    { name: "Chamisa Gold", hex: "#C9A247" },
    { name: "Desert Sage", hex: "#8F9B84" },
    { name: "Juniper", hex: "#4F5E4F" },
    { name: "Monsoon Blue", hex: "#5D7487" },
    { name: "Dusk Mauve", hex: "#8E6F78" },
    { name: "Wildberry", hex: "#7E2244" },
    { name: "Smoked Charcoal", hex: "#3A3836" },
  ],
  cabinets: [
    { name: "Gallery White", hex: "#F2EEE6" },
    { name: "Oat", hex: "#D9CDB8" },
    { name: "Sage Cabinet", hex: "#8C9A86" },
    { name: "Forest", hex: "#3E4B3F" },
    { name: "Ink Navy", hex: "#2F3A4A" },
    { name: "Clay", hex: "#9A5B3F" },
    { name: "Espresso", hex: "#4A3A30" },
    { name: "Charcoal", hex: "#33312F" },
  ],
  exteriorNeutrals: [
    { name: "Stucco White", hex: "#ECE5D8" },
    { name: "Sandia Cream", hex: "#E2D3BB" },
    { name: "Pueblo Tan", hex: "#CDB08A" },
    { name: "Mesa Taupe", hex: "#A8957D" },
    { name: "Juniper Gray", hex: "#8A8C84" },
    { name: "Storm Gray", hex: "#5F625F" },
    { name: "Iron Ore", hex: "#3A3A38" },
    { name: "Soft Black", hex: "#242322" },
  ],
  exteriorBold: [
    { name: "Sunset Terracotta", hex: "#B5552F" },
    { name: "Chile Red", hex: "#8E2B22" },
    { name: "Wildberry", hex: "#7E2244" },
    { name: "Marigold", hex: "#D9952B" },
    { name: "Vibrant Orange", hex: "#E65100" },
    { name: "Saguaro Green", hex: "#4E6B45" },
    { name: "Turquoise", hex: "#2E8A88" },
    { name: "Rio Grande Blue", hex: "#2F5D7C" },
  ],
};

export const kitchenCombos = [
  { name: "Bone & Ink", walls: "#EFE8DC", cabinets: "#2F3A4A" },
  { name: "Linen & Sage", walls: "#E3D7C4", cabinets: "#8C9A86" },
  { name: "Sand & Charcoal", walls: "#D2BC9A", cabinets: "#33312F" },
  { name: "Sage & Gallery", walls: "#8F9B84", cabinets: "#F2EEE6" },
  { name: "Greige & Clay", walls: "#BDB4A7", cabinets: "#9A5B3F" },
];

const interiorGroups = [{ label: "Curated wall colors", key: "interior" }];
const exteriorGroups = [
  { label: "Neutrals", key: "exteriorNeutrals" },
  { label: "Stronger colors", key: "exteriorBold" },
];

// Paint sheens. gloss drives the illustrated scenes; shine (0-1) drives the photo shader's specular strength and roughness.
export const sheens = [
  { id: "flat", label: "Flat", gloss: 0, shine: 0, note: "No shine. Hides surface flaws; common on ceilings." },
  { id: "eggshell", label: "Eggshell", gloss: 0.06, shine: 0.18, note: "A soft glow. A popular choice for living-area walls." },
  { id: "satin", label: "Satin", gloss: 0.12, shine: 0.38, note: "Smooth, velvety shine that is easier to wipe clean." },
  { id: "semi-gloss", label: "Semi-gloss", gloss: 0.22, shine: 0.65, note: "Noticeable shine; often used on trim, doors and cabinets." },
  { id: "high-gloss", label: "High gloss", gloss: 0.36, shine: 1, note: "Mirror-like shine that shows every detail of the surface." },
];

// Starting kitchen colours by part type (matches the grey shaker reference).
export const kitchenDefaults = {
  wall: "#E9E6E0", ceiling: "#F5F4F1", trim: "#F6F5F2", door: "#F6F5F2", upper: "#7E8C9E", pantry: "#7E8C9E", lower: "#7E8C9E", island: "#7E8C9E",
  counter: "#F4F3F0", backsplash: "#EFEDE8", hood: "#B9BDC1", floor: "#4A3326",
};
const kitchenSheens = { wall: "eggshell", ceiling: "flat", trim: "semi-gloss", door: "semi-gloss", upper: "semi-gloss", pantry: "semi-gloss", lower: "semi-gloss", island: "semi-gloss", counter: "high-gloss", backsplash: "high-gloss", hood: "satin", floor: "satin" };
const cabinetGroups = [{ label: "Cabinet colors", key: "cabinets" }, ...interiorGroups];
const isCab = (g) => ["upper", "pantry", "lower", "island"].includes(g);

function buildKitchen() {
  const surfaces = Object.fromEntries(
    KITCHEN_PARTS.map((p) => [p.id, { label: p.label, wall: p.group === "wall", sheen: kitchenSheens[p.group], groups: isCab(p.group) ? cabinetGroups : interiorGroups }])
  );
  return {
    label: "Kitchen",
    chapter: "02",
    intro: "Every door, drawer, frame, wall, trim piece and the ceiling can be painted on its own. Tap any part of the picture.",
    surfaces,
    original: Object.fromEntries(KITCHEN_PARTS.map((p) => [p.id, kitchenDefaults[p.group]])),
    targets: KITCHEN_GROUPS.map((g) => ({
      id: g.id,
      label: g.label,
      help: g.help,
      surfaces: KITCHEN_PARTS.filter((p) => g.match.includes(p.group)).map((p) => p.id),
      groups: g.match.some(isCab) ? cabinetGroups : interiorGroups,
    })),
    pick: "piece",
  };
}

function buildExterior(label, doorsHelp, trimHelp, extra = {}, overrides = {}, omit = []) {
  const base = {
    walls: { label: "Main walls", sheen: "flat", original: "#E6DAC6", help: "The large stucco wall areas of the building." },
    ...extra,
    trim: { label: "General trim", sheen: "semi-gloss", original: "#F1EBE0", help: trimHelp },
    soffit: { label: "Soffits", sheen: "flat", original: "#EFE7DA", help: "A soffit is the underside of the roof overhang — what you see when you stand below the roof edge and look up." },
    fascia: { label: "Fascia", sheen: "satin", original: "#2E2C2A", help: "The fascia is the vertical band that runs along the outer edge of the roof, facing outward." },
    windowTrim: { label: "Window trim", sheen: "semi-gloss", original: "#2E2C2A", help: "The casing and frames around each window." },
    doors: { label: "Doors", sheen: "semi-gloss", original: "#5B3F2E", help: doorsHelp },
  };
  Object.entries(overrides).forEach(([k, v]) => Object.assign(base[k], v));
  omit.forEach((k) => delete base[k]);
  const ids = Object.keys(base);
  return {
    label: `Exterior · ${label}`,
    model: label,
    chapter: "03",
    intro: "Each part of the building is defined separately, so changing one never recolors another.",
    surfaces: Object.fromEntries(ids.map((k) => [k, { label: base[k].label, sheen: base[k].sheen }])),
    original: Object.fromEntries(ids.map((k) => [k, base[k].original])),
    targets: ids.map((k) => ({ id: k, label: base[k].label, surfaces: [k], help: base[k].help, groups: exteriorGroups })),
    pick: Object.fromEntries(ids.map((k) => [k, k])),
  };
}

export const scenes = {
  living: {
    label: "Living room",
    chapter: "01",
    intro: "A New Mexico living room with vigas and a kiva fireplace. Paint the walls together, give the back wall an accent, or try the beams, lintels, fireplace and built-in.",
    surfaces: {
      leftWall: { label: "Window wall", wall: true },
      backWall: { label: "Accent wall", wall: true },
      rightWall: { label: "Side wall", wall: true },
      vigas: { label: "Vigas (ceiling beams)", sheen: "satin" },
      lintels: { label: "Wood lintels", sheen: "satin" },
      kiva: { label: "Kiva fireplace", sheen: "flat" },
      builtIn: { label: "Built-in cabinet", sheen: "satin" },
    },
    original: { leftWall: "#E9CFAE", backWall: "#E9CFAE", rightWall: "#E9CFAE", vigas: "#B87A44", lintels: "#8E5A3A", kiva: "#E2CFB8", builtIn: "#B48F6E" },
    targets: [
      { id: "walls", label: "All walls", surfaces: ["leftWall", "backWall", "rightWall"], help: "Every wall in the room, including the accent wall.", groups: interiorGroups },
      { id: "accent", label: "Accent wall", surfaces: ["backWall"], help: "The back wall behind the fireplace and built-in, painted on its own.", groups: interiorGroups },
      { id: "vigas", label: "Vigas", surfaces: ["vigas"], help: "Vigas are the round log beams that carry a traditional New Mexico roof.", groups: [{ label: "Cabinet colors", key: "cabinets" }, ...interiorGroups] },
      { id: "lintels", label: "Wood lintels", surfaces: ["lintels"], help: "The carved wood beams above the window, the built-in niche and the side opening.", groups: [{ label: "Cabinet colors", key: "cabinets" }, ...interiorGroups] },
      { id: "kiva", label: "Kiva fireplace", surfaces: ["kiva"], help: "The rounded corner fireplace and its plaster benches (bancos).", groups: interiorGroups },
      { id: "builtIn", label: "Built-in cabinet", surfaces: ["builtIn"], help: "The built-in cabinet with punched-tin door panels and its top shelf.", groups: [{ label: "Cabinet colors", key: "cabinets" }, ...interiorGroups] },
    ],
    pick: { leftWall: "walls", rightWall: "walls", backWall: "accent", vigas: "vigas", lintels: "lintels", kiva: "kiva", builtIn: "builtIn" },
  },
  kitchen: buildKitchen(),
  exterior: buildExterior("Two-story home", "The front entry door and the garage door.", "Corner boards, the band between floors, the base band, the porch columns and gable, and the frames around the doors.", {}, {
    walls: { original: "#B8B2AC", help: "The large smooth stucco wall areas of the house." },
    trim: { original: "#F0EEE9" },
    soffit: { original: "#E9E6DF" },
    fascia: { original: "#4A4A4A" },
    windowTrim: { original: "#F0EEE9", help: "The white casings and sashes around each window." },
    doors: { original: "#4A3020" },
  }),
  exteriorSingle: buildExterior("One-story New Mexico home", "The carved wooden front door under the portal.", "The portal (porch) posts, the beam and corbels, and the canales (roof drain spouts).", {}, {
    walls: { original: "#C9A878", help: "The smooth stucco walls of the pueblo-style house." },
    trim: { original: "#6E4220", sheen: "satin" },
    fascia: { label: "Parapet caps", original: "#D8BB8E", help: "The rounded tops of the parapet walls that hide the flat roof, like a fascia on a pitched roof." },
    windowTrim: { original: "#6E4521", help: "The dark wood frames around each window." },
    doors: { original: "#643818" },
  }, ["soffit"]),
  commercial: buildExterior("Commercial building", "The entrance door frames and the side service door.", "Pilasters (the vertical columns between windows), the band between the floors and the dark base band.", {
    accent: { label: "Accent tower", sheen: "satin", original: "#9A4A40", help: "The raised feature tower above the entrance." },
  }, {
    walls: { original: "#D8CEC2" },
    trim: { original: "#D8CEC2" },
    soffit: { label: "Soffits & canopy", original: "#8A877E", help: "The undersides of the tower cap, the roof edge and the entrance canopy." },
    fascia: { original: "#6E6D6B", help: "The roof-edge bands, the awnings over the shop windows and the canopy edge." },
    windowTrim: { original: "#4F524D", help: "The metal frames and mullions of every window." },
    doors: { original: "#5E584E" },
  }),
};

// Every scene (rooms + building types). Tabs group the three exterior models under one "Exterior" tab.
export const sceneOrder = ["living", "kitchen", "exterior", "exteriorSingle", "commercial"];
export const exteriorModels = ["exterior", "exteriorSingle", "commercial"];
export const tabs = [
  { id: "living", label: "Living room", chapter: "01" },
  { id: "kitchen", label: "Kitchen", chapter: "02" },
  { id: "exterior", label: "Exterior", chapter: "03" },
];

// Lowercases a label for use mid-sentence, keeping proper nouns capitalized.
export const inSentence = (label) => label.toLowerCase().replace(/\bnew mexico\b/g, "New Mexico");
